import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './ResourcesPage.module.css';
import ListLoadError from '../../components/admin/ListLoadError';

const TABS = ['FAQs', 'Videos', 'PDFs', 'Categories'];
const TYPE_MAP = { FAQs: 'faq', Videos: 'video', PDFs: 'pdf' };

const emptyResource = { type: 'faq', title: '', content: '', categoryId: '', videoUrl: '', pdfUrl: '', thumbnailUrl: '', displayOrder: 0, isPublished: true, comingSoon: false };
const emptyCategory = { name: '', description: '', icon: '', imageUrl: '', displayOrder: 0 };

const ResourcesPage = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('FAQs');
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyResource);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const isCategory = activeTab === 'Categories';
  const currentType = TYPE_MAP[activeTab];

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [resRes, catRes] = await Promise.all([
        api.get('/admin/resources'),
        api.get('/admin/resource-categories')
      ]);
      setResources(resRes.resources || []);
      setCategories(catRes.categories || []);
    } catch (err) {
      setLoadError(true);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredItems = isCategory
    ? categories.sort((a, b) => a.displayOrder - b.displayOrder)
    : resources.filter((r) => r.type === currentType).sort((a, b) => a.displayOrder - b.displayOrder);

  const getCategoryName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const openCreate = () => {
    setEditingItem(null);
    setFormData(isCategory ? { ...emptyCategory } : { ...emptyResource, type: currentType });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    if (isCategory) {
      setFormData({ name: item.name || '', description: item.description || '', icon: item.icon || '', imageUrl: item.imageUrl || '', displayOrder: item.displayOrder || 0 });
    } else {
      setFormData({ type: item.type, title: item.title || '', content: item.content || '', categoryId: item.categoryId || '', videoUrl: item.videoUrl || '', pdfUrl: item.pdfUrl || '', thumbnailUrl: item.thumbnailUrl || '', displayOrder: item.displayOrder || 0, isPublished: item.isPublished, comingSoon: item.comingSoon || false });
    }
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingItem(null); };

  const handleSave = async () => {
    if (isCategory && !formData.name.trim()) { toast.error('Name is required'); return; }
    if (!isCategory && !formData.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (isCategory) {
        if (editingItem) {
          const res = await api.patch(`/admin/resource-categories/${editingItem.id}`, formData);
          setCategories((prev) => prev.map((c) => c.id === editingItem.id ? res.category : c));
          toast.success('Category updated');
        } else {
          const res = await api.post('/admin/resource-categories', formData);
          setCategories((prev) => [...prev, res.category]);
          toast.success('Category created');
        }
      } else {
        if (editingItem) {
          const res = await api.patch(`/admin/resources/${editingItem.id}`, formData);
          setResources((prev) => prev.map((r) => r.id === editingItem.id ? res.resource : r));
          toast.success('Resource updated');
        } else {
          const res = await api.post('/admin/resources', formData);
          setResources((prev) => [...prev, res.resource]);
          toast.success('Resource created');
        }
      }
      closeModal();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      const endpoint = isCategory ? `/admin/resource-categories/${item.id}` : `/admin/resources/${item.id}`;
      await api.delete(endpoint);
      if (isCategory) {
        setCategories((prev) => prev.filter((c) => c.id !== item.id));
      } else {
        setResources((prev) => prev.filter((r) => r.id !== item.id));
      }
      toast.success('Deleted');
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      if (isCategory) {
        const res = await api.patch(`/admin/resource-categories/${item.id}/publish`);
        setCategories((prev) => prev.map((c) => c.id === item.id ? res.category : c));
      } else {
        const res = await api.patch(`/admin/resources/${item.id}/publish`);
        setResources((prev) => prev.map((r) => r.id === item.id ? res.resource : r));
      }
    } catch (err) {
      toast.error('Failed to toggle publish');
    }
  };

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Resources</h1>
          <p className={styles.subtitle}>Manage FAQs, videos, PDFs, and categories</p>
        </div>
        <button className={styles.addBtn} onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Add {isCategory ? 'Category' : activeTab.slice(0, -1)}
        </button>
      </header>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button key={tab} className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`} onClick={() => setActiveTab(tab)}>
            {tab}
            <span className={styles.tabCount}>{loadError ? '\u2014' : (tab === 'Categories' ? categories.length : resources.filter((r) => r.type === TYPE_MAP[tab]).length)}</span>
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className={styles.empty}>Loading...</div>
      ) : loadError ? (
        <ListLoadError message="Could not load resources" onRetry={loadData} />
      ) : filteredItems.length === 0 ? (
        <div className={styles.empty}>No {activeTab.toLowerCase()} yet. Click "Add" to create one.</div>
      ) : (
        <div className={styles.list}>
          {filteredItems.map((item) => (
            <div key={item.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>{isCategory ? item.name : item.title}</span>
                {!isCategory && <span className={styles.rowMeta}>{getCategoryName(item.categoryId)}</span>}
                {isCategory && item.icon && <span className={styles.rowMeta}>Icon: {item.icon}</span>}
              </div>
              <div className={styles.rowActions}>
                <button className={`${styles.badge} ${item.isPublished ? styles.published : styles.draft}`} onClick={() => handleTogglePublish(item)}>
                  {item.isPublished ? 'Published' : 'Draft'}
                </button>
                <button className={styles.editBtn} onClick={() => openEdit(item)}>Edit</button>
                <button className={styles.deleteBtn} onClick={() => setDeleteConfirm(item)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className={styles.overlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Delete {isCategory ? 'Category' : 'Resource'}</h3>
            <p className={styles.modalText}>Are you sure you want to delete "{isCategory ? deleteConfirm.name : deleteConfirm.title}"? This cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{editingItem ? 'Edit' : 'Add'} {isCategory ? 'Category' : activeTab.slice(0, -1)}</h3>

            {isCategory ? (
              <div className={styles.form}>
                <label className={styles.label}>Name *
                  <input className={styles.input} value={formData.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Category name" />
                </label>
                <label className={styles.label}>Description
                  <textarea className={styles.textarea} value={formData.description} onChange={(e) => updateField('description', e.target.value)} placeholder="Short description" rows={2} />
                </label>
                <label className={styles.label}>Icon
                  <input className={styles.input} value={formData.icon} onChange={(e) => updateField('icon', e.target.value)} placeholder="e.g. rocket, stethoscope" />
                </label>
                <label className={styles.label}>Image URL
                  <input className={styles.input} value={formData.imageUrl} onChange={(e) => updateField('imageUrl', e.target.value)} placeholder="https://..." />
                </label>
                {(currentType === 'video' || currentType === 'pdf') && (
                  <label className={styles.label} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <span>Coming Soon</span>
                    <div 
                      onClick={() => updateField('comingSoon', !formData.comingSoon)}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px',
                        backgroundColor: formData.comingSoon ? '#f59e0b' : '#d1d5db',
                        position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s'
                      }}
                    >
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        backgroundColor: '#fff', position: 'absolute', top: '2px',
                        left: formData.comingSoon ? '22px' : '2px', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </label>
                )}
                <label className={styles.label}>Display Order
                  <input className={styles.input} type="number" value={formData.displayOrder} onChange={(e) => updateField('displayOrder', parseInt(e.target.value) || 0)} />
                </label>
              </div>
            ) : (
              <div className={styles.form}>
                <label className={styles.label}>Title *
                  <input className={styles.input} value={formData.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Resource title" />
                </label>
                <label className={styles.label}>Category
                  <select className={styles.input} value={formData.categoryId} onChange={(e) => updateField('categoryId', e.target.value)}>
                    <option value="">— No category —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                {currentType === 'faq' && (
                  <label className={styles.label}>Content / Answer
                    <textarea className={styles.textarea} value={formData.content} onChange={(e) => updateField('content', e.target.value)} placeholder="FAQ answer..." rows={5} />
                  </label>
                )}
                {currentType === 'video' && (
                  <>
                    <label className={styles.label}>Video URL
                      <input className={styles.input} value={formData.videoUrl} onChange={(e) => updateField('videoUrl', e.target.value)} placeholder="https://..." />
                    </label>
                    <label className={styles.label}>Thumbnail URL
                      <input className={styles.input} value={formData.thumbnailUrl} onChange={(e) => updateField('thumbnailUrl', e.target.value)} placeholder="https://..." />
                    </label>
                  </>
                )}
                {currentType === 'pdf' && (
                  <>
                    <label className={styles.label}>PDF URL
                      <input className={styles.input} value={formData.pdfUrl} onChange={(e) => updateField('pdfUrl', e.target.value)} placeholder="https://..." />
                    </label>
                    <div className={styles.uploadDropzone}>
                      <span className={styles.dropzoneLabel}>Or upload a PDF file</span>
                      <label className={styles.dropzoneArea}>
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploading(true);
                            try {
                              const fd = new FormData();
                              fd.append('pdf', file);
                              const res = await api.post('/admin/resources/upload-pdf', fd, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              if (res.url) {
                                updateField('pdfUrl', res.url);
                                toast.success('PDF uploaded');
                              }
                            } catch (err) {
                              toast.error('Upload failed: ' + (err.message || 'Unknown error'));
                            } finally {
                              setUploading(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        {uploading ? (
                          <span className={styles.dropzoneText}>Uploading...</span>
                        ) : (
                          <>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5 }}>
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span className={styles.dropzoneText}>Click to upload PDF</span>
                            <span className={styles.dropzoneHint}>Max 100MB</span>
                          </>
                        )}
                      </label>
                    </div>
                    <label className={styles.label}>Description
                      <textarea className={styles.textarea} value={formData.content} onChange={(e) => updateField('content', e.target.value)} placeholder="Description..." rows={3} />
                    </label>
                  </>
                )}
                {(currentType === 'video' || currentType === 'pdf') && (
                  <label className={styles.label} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <span>Coming Soon</span>
                    <div 
                      onClick={() => updateField('comingSoon', !formData.comingSoon)}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px',
                        backgroundColor: formData.comingSoon ? '#f59e0b' : '#d1d5db',
                        position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s'
                      }}
                    >
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        backgroundColor: '#fff', position: 'absolute', top: '2px',
                        left: formData.comingSoon ? '22px' : '2px', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </label>
                )}
                <label className={styles.label}>Display Order
                  <input className={styles.input} type="number" value={formData.displayOrder} onChange={(e) => updateField('displayOrder', parseInt(e.target.value) || 0)} />
                </label>
              </div>
            )}

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
